import React, { useEffect, useState } from 'react';
import { Text } from '@fluentui/react';
import { DirectionsRenderer, InfoWindow, Marker } from '@react-google-maps/api';

import ItemRuta from '../../models/ItemRuta';
import ItemRutaActuacion from '../../models/ItemRutaActuacion';

const getLatLng = (value: string) : google.maps.LatLng => {
    const valueArray : string[] = value.split(',');
    return new google.maps.LatLng(parseFloat(valueArray[0]), parseFloat(valueArray[1]));
};

interface IGoogleRouteComponentProps {
    route: ItemRuta,
    evolution: boolean;
};

const GoogleRouteComponent = (props: IGoogleRouteComponentProps) : JSX.Element => {
    const [selectedIndex, setSelectedIndex] = useState<number>();
    const [finishedDirections, setFinishedDirections] = useState<google.maps.DirectionsResult>();
    const [directions, setDirections] = useState<google.maps.DirectionsResult>();

    useEffect(() => {
        const getDirection = (posicionGPSInicial: string, posicionGPSFinal: string, waypoints: string[]) : Promise<google.maps.DirectionsResult> => {
            return new Promise((resolve, reject) => {
                const googleDataPositionInicio : google.maps.LatLng = getLatLng(posicionGPSInicial);
                const googleDataPositionFin : google.maps.LatLng = getLatLng(posicionGPSFinal);
                const googleWaypoints : google.maps.DirectionsWaypoint[] = waypoints.map((waypoint: string) => {
                    return {
                        location: getLatLng(waypoint),
                        stopover: true,
                    };
                });

                const directionsService : google.maps.DirectionsService = new google.maps.DirectionsService();
                return directionsService.route({
                    origin: googleDataPositionInicio,
                    destination: googleDataPositionFin,
                    travelMode: google.maps.TravelMode.DRIVING,
                    waypoints: googleWaypoints,
                }, (result: google.maps.DirectionsResult | null, status: google.maps.DirectionsStatus) => {
                    if (result && status === google.maps.DirectionsStatus.OK) {
                        resolve(result);
                    }
                    else {
                        console.error(`error fetching direction ${result}: ${posicionGPSInicial}:${posicionGPSFinal}`);
                        reject();
                    }
                });
            });
        };

        const getDirections = () : Promise<google.maps.DirectionsResult> => {
            return new Promise(async (resolve) => {
                let posicionGPSInicial : string = "";
                let posicionGPSFinal : string = "";
                let waypoints : string[] = [];

                if (props.route.SalidaPosicionGPS) {
                    posicionGPSInicial = props.route.SalidaPosicionGPS;
                }
                else {
                    posicionGPSInicial = props.route.Actuaciones[0].PosicionGPS;
                }

                for (let actuacion of props.route.Actuaciones) {
                    waypoints.push(actuacion.PosicionGPS);
                }

                if (props.route.IdaYVuelta) {
                    if (props.route.SalidaPosicionGPS) {
                        posicionGPSFinal = props.route.SalidaPosicionGPS;
                    }
                    else {
                        posicionGPSFinal = props.route.Actuaciones[0].PosicionGPS;
                    }
                }
                else {
                    posicionGPSFinal = props.route.Actuaciones[props.route.Actuaciones.length - 1].PosicionGPS;
                }

                let directions : google.maps.DirectionsResult = await getDirection(posicionGPSInicial, posicionGPSFinal, waypoints);
                resolve(directions);
            });
        };

        const getFinishedDirections = () : Promise<google.maps.DirectionsResult> => {
            return new Promise(async (resolve, reject) => {
                let posicionGPSInicial : string = "";
                let posicionGPSFinal : string = "";
                let waypoints : string[] = [];

                if (props.route.SalidaPosicionGPS) {
                    posicionGPSInicial = props.route.SalidaPosicionGPS;
                }
                else {
                    posicionGPSInicial = props.route.Actuaciones[0].PosicionGPS;
                }

                for (let actuacion of props.route.Actuaciones) {
                    if (actuacion.Estado === 30) {
                        waypoints.push(actuacion.PosicionGPS);
                    }
                }

                if (!props.route.Actuaciones.some((actuacion: ItemRutaActuacion) => actuacion.Estado !== 30)) {
                    if (props.route.IdaYVuelta) {
                        if (props.route.SalidaPosicionGPS) {
                            posicionGPSFinal = props.route.SalidaPosicionGPS;
                        }
                        else {
                            posicionGPSFinal = props.route.Actuaciones[0].PosicionGPS;
                        }
                    }
                    else {
                        posicionGPSFinal = props.route.Actuaciones[props.route.Actuaciones.length - 1].PosicionGPS;
                    }
                }
                else {
                    posicionGPSFinal = waypoints.length ? waypoints[waypoints.length - 1] : posicionGPSInicial;
                }

                if (waypoints.length) {
                    let directions : google.maps.DirectionsResult = await getDirection(posicionGPSInicial, posicionGPSFinal, waypoints);
                    resolve(directions);
                }
                else {
                    reject();
                }
            });
        };

        getDirections().then((directions: google.maps.DirectionsResult) => {
            setDirections(directions);
            if (props.evolution) {
                getFinishedDirections().then((directions: google.maps.DirectionsResult) => {
                    setFinishedDirections(directions);
                });
            }
        });
    }, [props.route, props.evolution]);

    return (
        <>
            {
                props.route.SalidaPosicionGPS ? (
                    <Marker 
                        position={getLatLng(props.route.SalidaPosicionGPS)}
                        label={{
                            color: "#000000",
                            fontSize: "20px",
                            fontWeight: "bold",
                            fontFamily: "DINNextRoundedLTProMediumRegular",
                            className: "mapIconLabel",
                            text: "S",
                        }}
                        icon={{
                            path: "M47,126.6c-0.3-0.7-6.2-6.4-10.4-11.7 C11.7,81.9-16.2,51.1,16.3,13.7C30.7-0.2,48.4-1,66.7,5.5c51.3,29.7,14.2,78.2-10.2,110L47,126.6z M62.4,75.9 c34.2-24.5,7.8-71.6-29.8-56c-11,5.9-16.4,16-16.6,28.1c-0.5,11.7,5.7,21.5,15.5,27.5c7.1,3.8,10.2,4.5,18.1,4.1 C54.8,79.2,57.8,78.3,62.4,75.9z",
                            scale: 0.3,
                            anchor: new google.maps.Point(50, 100),
                            fillColor: "#000000",
                            fillOpacity: 1,
                            strokeColor: "#000000",
                            strokeOpacity: 1,
                        }}
                        onClick={() => {    
                            setSelectedIndex(-1);
                        }}>
                        {
                            (selectedIndex === -1 && props.route.SalidaDireccionNombre) ? (
                                <InfoWindow position={getLatLng(props.route.SalidaPosicionGPS)}>
                                    <Text variant="medium">
                                        {props.route.SalidaDireccionNombre}
                                    </Text>
                                </InfoWindow>
                            ) : (
                                null
                            )
                        }
                    </Marker>
                ) : (
                    null
                )
            }
            {
                props.route.Actuaciones.map((actuacion: ItemRutaActuacion, index: number) => (
                    <Marker 
                        key={index}
                        position={getLatLng(actuacion.PosicionGPS)}
                        label={{
                            color: (props.evolution && actuacion.Estado === 30) ? "#666666" : props.route.ColorMarcador ? props.route.ColorMarcador : props.route.Color ? props.route.Color : "#ed6d63",
                            fontSize: actuacion.Posicion.toString().length > 1 ? "16px" : "20px",
                            fontWeight: "bold",
                            fontFamily: "DINNextRoundedLTProMediumRegular",
                            className: "mapIconLabel",
                            text: actuacion.Posicion.toString(),
                        }}
                        icon={{
                            path: "M47,126.6c-0.3-0.7-6.2-6.4-10.4-11.7 C11.7,81.9-16.2,51.1,16.3,13.7C30.7-0.2,48.4-1,66.7,5.5c51.3,29.7,14.2,78.2-10.2,110L47,126.6z M62.4,75.9 c34.2-24.5,7.8-71.6-29.8-56c-11,5.9-16.4,16-16.6,28.1c-0.5,11.7,5.7,21.5,15.5,27.5c7.1,3.8,10.2,4.5,18.1,4.1 C54.8,79.2,57.8,78.3,62.4,75.9z",
                            scale: 0.3,
                            anchor: new google.maps.Point(50, 100),
                            fillColor: (props.evolution && actuacion.Estado === 30) ? "#666666" : props.route.ColorMarcador ? props.route.ColorMarcador : props.route.Color ? props.route.Color : "#ed6d63",
                            fillOpacity: 1,
                            strokeColor: (props.evolution && actuacion.Estado === 30) ? "#666666" : props.route.ColorMarcador ? props.route.ColorMarcador : props.route.Color ? props.route.Color : "#ed6d63",
                            strokeOpacity: 1,
                        }}
                        onClick={() => {    
                            setSelectedIndex(index);
                        }}>
                        {
                            (selectedIndex === index) ? (
                                <InfoWindow position={getLatLng(actuacion.PosicionGPS)}>
                                    <>
                                        <Text variant="medium">
                                            <strong>
                                                {actuacion.Referencia ? actuacion.Referencia : actuacion.IdTarea} - {actuacion.Nombre}
                                            </strong>
                                        </Text>
                                        <br/>
                                        <Text>
                                            {actuacion.DireccionCompleta}
                                        </Text>
                                    </>
                                </InfoWindow>
                            ) : (
                                null
                            )
                        }
                    </Marker>
                ))
            }
            {
                props.route.UltimaPosicionGPS ? (
                    <Marker 
                        position={getLatLng(props.route.UltimaPosicionGPS)}
                        icon={{
                            path: `M213.333333,3.55271368e-14 C331.15408,3.55271368e-14 426.666667,95.5125867 426.666667,213.333333 C426.666667,331.15408 331.15408,426.666667 213.333333,426.666667 C95.5125867,426.666667 3.55271368e-14,331.15408 3.55271368e-14,213.333333 C3.55271368e-14,95.5125867 95.5125867,3.55271368e-14 213.333333,3.55271368e-14 Z M213.333333,106.666667 C154.42296,106.666667 106.666667,154.42296 106.666667,213.333333 C106.666667,272.243707 154.42296,320 213.333333,320 C272.243707,320 320,272.243707 320,213.333333 C320,154.42296 272.243707,106.666667 213.333333,106.666667 Z`,
                            scale: 0.075,
                            anchor: new google.maps.Point(50, 100),
                            fillColor: props.route.ColorMarcador ? props.route.ColorMarcador : props.route.Color ? props.route.Color : "#ed6d63",
                            fillOpacity: 1,
                            strokeColor: props.route.ColorMarcador ? props.route.ColorMarcador : props.route.Color ? props.route.Color : "#ed6d63",
                            strokeOpacity: 1,
                        }}
                        onClick={() => {    
                            setSelectedIndex(-2);
                        }}>
                        {
                            (selectedIndex === -2) ? (
                                <InfoWindow position={getLatLng(props.route.UltimaPosicionGPS)}>
                                    <>
                                        <Text variant="medium">
                                            <strong>
                                                {props.route.NombreUsuarioAsignado}
                                            </strong>
                                        </Text>
                                        <br/>
                                        <Text>
                                            {props.route.UltimaPosicionGPS}
                                        </Text>
                                    </>
                                </InfoWindow>
                            ) : (
                                null
                            )
                        }
                    </Marker>
                ) : (
                    null
                )
            }
            {
                finishedDirections ? (
                    <DirectionsRenderer 
                        directions={finishedDirections}
                        options={{
                            polylineOptions: {
                                strokeColor: "#666666",
                                strokeOpacity: 1,
                                strokeWeight: 5,
                            },
                            markerOptions: {
                                visible: false,
                            },
                        }} />
                ) : (
                    null
                )
            }
            {
                directions ? (
                    <DirectionsRenderer 
                        directions={directions}
                        options={{
                            polylineOptions: {
                                strokeColor: props.route.Color ? props.route.Color : "#ed6d63",
                                strokeOpacity: 1,
                                strokeWeight: 5,
                            },
                            markerOptions: {
                                visible: false,
                            },
                        }} />
                ) : (
                    null
                )
            }
        </>
    );
};

export default GoogleRouteComponent;
